import { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router';
import { styled } from '@mui/material/styles';
import { StatsigProvider } from '@statsig/react-bindings';
import useNotificationHub from '../../hooks/useNotificationHub';
import { useDispatch, useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import { loadSidebarPeers } from '../../features/sidebar-peers/api';
import DashboardNavbar from './DashboardNavbar';
import DashboardSidebar from './DashboardSidebar';
import OrgSwitcherPopover from './OrgSwitcherPopover';
import buildNavConfig from './SidebarConfig';
import useAuth from '@/hooks/useAuth';
import statsigClientInstance, { updateStatsigUser } from '@/utils/statsig';

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 64; // 92;

const RootStyle = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden'
});

const MainStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(5),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 16,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3)
  }
}));

function DashboardContent() {
  const [open, setOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );
  useNotificationHub();

  const dispatch = useDispatch();
  const { user } = useAuth();
  const { activeOrgGuid, memberships } = useSelector(
    (state: RootState) => state.organizationContext
  );
  const activeMembership = memberships.find((m) => m.organizationGuid === activeOrgGuid);
  const { circles } = useSelector((state: RootState) => state.sidebarPeers);

  useEffect(() => {
    dispatch(loadSidebarPeers());
  }, [dispatch, activeOrgGuid]);

  const handleToggleCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  return (
    <>
      <DashboardNavbar onOpenSidebar={() => setOpen(true)} isCollapsed={isCollapsed} />
      <DashboardSidebar
        isOpenSidebar={open}
        onCloseSidebar={() => setOpen(false)}
        isCollapsed={isCollapsed}
        onToggleCollapse={handleToggleCollapse}
        navConfig={buildNavConfig(circles, user?.guid, activeMembership)}
        footer={<OrgSwitcherPopover collapsed={isCollapsed} />}
      />
      <MainStyle>
        <Outlet />
      </MainStyle>
    </>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();
  // Created/initialized once; subsequent renders just return the existing singleton.
  const client = useMemo(() => statsigClientInstance(user), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the Statsig user in sync with the authenticated user. Updating the client
  // triggers StatsigProvider's internal state update, so this must run in an effect,
  // not during render.
  useEffect(() => {
    updateStatsigUser(user);
  }, [user]);

  return (
    <RootStyle>
      <StatsigProvider client={client}>
        <DashboardContent />
      </StatsigProvider>
    </RootStyle>
  );
}
